import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";
import "../../styles/buttons.css";

const buttonVariants = cva(
  "collectr-button",
  {
    variants: {
      variant: {
        default: "collectr-button-primary",
        destructive:
          "collectr-button-destructive",
        "destructive-outline":
          "collectr-button-destructive-outline",
        outline:
          "collectr-button-secondary",
        secondary: "collectr-button-secondary",
        ghost: "collectr-button-subtle",
        info: "collectr-button-info",
        more: "collectr-button-more",
        link: "collectr-button-link",
      },
      size: {
        default: "collectr-button-standard",
        sm: "collectr-button-compact",
        // Compatibility alias: only standard and compact geometry remain.
        lg: "collectr-button-standard",
        icon: "collectr-button-icon",
        "icon-sm": "collectr-button-icon collectr-button-compact",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Keep the label's geometry while an action is running. */
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, disabled, children, onClick, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const unavailable = disabled || loading;
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
        disabled={asChild ? undefined : unavailable}
        aria-disabled={asChild && unavailable ? true : props["aria-disabled"]}
        aria-busy={loading || props["aria-busy"]}
        onClick={event => {
          if (unavailable) { event.preventDefault(); event.stopPropagation(); return; }
          onClick?.(event);
        }}
      >
        {loading && !asChild ? <span className="collectr-button-loading-content">
          <span className="collectr-button-loading-label">{children}</span>
          <span className="collectr-button-spinner" aria-hidden="true" />
        </span> : children}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
